import { useMemo } from 'react';

// 👻 추후 없어질 가데이터
import getBilingPolicyInfo from 'jsons/biling_table/bilingPolicy.json';
import mapMenu from 'jsons/menu/map.json';
import routeMenu from 'jsons/menu/route.json';
import searchMenu from 'jsons/menu/search.json';

import { formatThreeComma } from 'utils/number/formatNumberUtil';

// ─── 공통 메뉴 맵 ──────────────────────────────────────────────────────────────

/** 검색·경로·지도 메뉴의 모든 pages를 하나로 합친 맵 */
const allMenuPages = {
  ...searchMenu.pages,
  ...routeMenu.pages,
  ...mapMenu.pages,
};

/**
 * menuKey → 카테고리(검색·탐색·지도) 매핑 테이블
 * 모듈 로딩 시 한 번만 구성됩니다.
 * @type {Record<string, string>}
 */
const categoryMap = {};

const buildCategoryMap = (menuObj) => {
  if (!menuObj) return;
  for (const [category, keys] of Object.entries(menuObj)) {
    for (const key of keys) {
      categoryMap[key] = category;
    }
  }
};
buildCategoryMap(searchMenu.menu);
buildCategoryMap(routeMenu.menu);
buildCategoryMap(mapMenu.menu);

/**
 * API 항목에 해당하는 메뉴 페이지 정보를 반환합니다.
 *
 * @param {object} apiItem - 정책 API 항목
 * @returns {object} 메뉴 페이지 정보 (path, title, description 등)
 */
const getMenuInfo = (apiItem) => {
  if (apiItem.menuKey && allMenuPages[apiItem.menuKey]) {
    return allMenuPages[apiItem.menuKey];
  }
  const page = Object.values(allMenuPages).find(
    (p) => p.path === apiItem.path || p.title === apiItem.unitApi
  );
  return page || {};
};

/** 정책 그룹명 → 화면 표시 레이블 변환 */
const groupLabelMap = {
  검색: '검색',
  탐색: '경로',
  지도: '지도',
};

// ─── 슬라이더 보조 함수 ────────────────────────────────────────────────────────

/**
 * 숫자를 '만/천/백' 등 간략 표기로 변환합니다.
 *
 * @param {number} n
 * @returns {string}
 */
const formatCompactNumber = (n) => {
  if (n >= 10000) return `${n / 10000}만`;
  if (n >= 1000) return `${n / 1000}천`;
  if (n >= 100 && n % 100 === 0) return n === 100 ? '100' : `${n / 100}백`;
  return `${n}`;
};

/**
 * 슬라이더 마커 라벨을 생성합니다.
 *
 * @param {number|string|undefined} val - 원본 값
 * @param {'일'|'월'} unit             - 시간 단위
 * @returns {string}
 */
const formatSliderMarker = (val, unit) => {
  if (
    val === undefined ||
    isNaN(val) ||
    val === '미제공' ||
    val === '개별 문의'
  )
    return '';
  const num = Number(val);
  if (num === 0) return '무료';
  return unit === '일'
    ? `${formatCompactNumber(num)}/일`
    : `${formatCompactNumber(num)}/월`;
};

/**
 * 주어진 숫자를 자릿수 기준으로 올림합니다.
 *
 * @param {number} num
 * @returns {number}
 */
const roundUpToMagnitude = (num) => {
  if (num <= 0) return 6_000_000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(num)));
  return Math.ceil(num / magnitude) * magnitude;
};

// ─── 순수 계산 함수 (named export) ────────────────────────────────────────────
// 아래 함수들은 React 컨텍스트 외부(Recoil atom default 등)에서도 사용됩니다.

/**
 * 요금 계산기 테이블의 행 데이터를 생성합니다.
 * 각 행: [group, category, api, charge_per_call, month_use(슬라이더), cost]
 *
 * @returns {Array<Array<object|null>>}
 */
export const calcBilingPlanCalcInfo = () => {
  const allRows = [];

  getBilingPolicyInfo.result.policyList.forEach(({ group, api }) => {
    const groupLabel = groupLabelMap[group] || group;
    const activeApis = api.filter(
      (el) => el.premium !== undefined && el.premium.value !== '미제공'
    );

    let currentCategory = null;
    let currentCategoryStartRowIdx = -1;

    activeApis.forEach((el, idx) => {
      const menuInfo = getMenuInfo(el);
      const unitApi = el.unitApi ?? menuInfo.title;
      const desc = el.message || menuInfo.description || '';
      const message = desc.split('\n')[0];
      const category = categoryMap[el.menuKey] || el.category || '';
      const path = menuInfo.path || el.path;

      const freeDailyStr = formatSliderMarker(el.free?.value, '일');
      const startMonthlyStr = formatSliderMarker(el.lite?.value, '월');

      const freeMonthlyPercent =
        el.free && !isNaN(el.free.value) ? el.free.value * 30 : 0;
      const startMonthlyPercent =
        el.lite && !isNaN(el.lite.value) ? el.lite.value : 0;
      const exceptionLimit = el.premium?.exception || 0;

      let initialValue = 0;
      if (el.premium?.value === undefined || isNaN(Number(el.premium.value))) {
        initialValue = 0;
      } else if (exceptionLimit > 0) {
        initialValue = exceptionLimit;
      } else if (startMonthlyPercent === 0) {
        initialValue = freeMonthlyPercent > 0 ? freeMonthlyPercent : 0;
      } else {
        initialValue = startMonthlyPercent;
      }

      let calculatedMax = 6_000_000;
      if (startMonthlyPercent > 0) {
        calculatedMax = startMonthlyPercent * 2;
      } else if (freeMonthlyPercent > 0) {
        calculatedMax = freeMonthlyPercent * 10;
      }

      const sliderConfig = {
        type: 'month_use',
        slider: {
          charge: el.premium?.value === 0 ? 'free' : undefined,
          min: 0,
          // max: roundUpToMagnitude(calculatedMax),
          max: calculatedMax,
          free: freeDailyStr
            ? { value: freeDailyStr, percent: freeMonthlyPercent }
            : undefined,
          fix: startMonthlyStr
            ? { value: startMonthlyStr, percent: startMonthlyPercent }
            : undefined,
          exceptionLimit: el.premium?.exception || 0,
        },
        value: initialValue,
      };

      const row = [
        idx === 0
          ? { type: 'group', rowSpan: activeApis.length, value: groupLabel }
          : null,
        { type: 'category', value: category, rowSpan: 1 },
        {
          type: 'api',
          value: unitApi,
          message,
          path,
          date: el.premium?.date || el.date,
        },
        sliderConfig,
        {
          type: 'charge_per_call',
          value: el.premium.value,
          exception: el.premium?.exception,
        },
        { type: 'cost', value: 0 },
      ];

      if (category && category === currentCategory) {
        const categoryCell = allRows[currentCategoryStartRowIdx].find(
          (c) => c && c.type === 'category'
        );
        if (categoryCell) categoryCell.rowSpan++;
        row[1] = null;
      } else {
        currentCategory = category;
        currentCategoryStartRowIdx = allRows.length;
      }

      allRows.push(row);
    });
  });

  return allRows;
};

/**
 * 요금제 유형별 테이블 행 데이터를 생성합니다.
 *
 * @param {'free'|'lite'|'premium'|'license'} type - 요금제 유형
 * @returns {Array<object>}
 */
export const calcBillingPlanTypeInfo = (type) => {
  const policyList = getBilingPolicyInfo.result.policyList;

  const groupToKey = { 검색: 'search', 탐색: 'route', 지도: 'map' };
  const groupLists = { search: [], route: [], map: [] };

  for (const group of policyList) {
    const prefix = groupToKey[group.group];
    if (!prefix) continue;

    let currentCategory = null;
    let currentCategoryStartIdx = -1;

    for (const apiItem of group.api) {
      const planValue = apiItem[type]?.value;
      if (planValue === '미제공' || planValue === undefined) continue;

      const menuInfo = getMenuInfo(apiItem);
      const unitApi = apiItem.unitApi ?? menuInfo.title;
      const path = menuInfo.path || apiItem.path;
      const category = categoryMap[apiItem.menuKey] || apiItem.category || '';

      const newRow = {
        [`${prefix}_unitApi`]: unitApi,
        [`${prefix}_category`]: category,
        [`${prefix}_value`]: planValue ?? 0,
        [`${prefix}_exception`]: apiItem[type]?.exception,
        [`${prefix}_overage`]: apiItem?.premium?.value ?? 0,
        [`${prefix}_path`]: path,
        [`${prefix}_date`]: apiItem[type]?.date || apiItem.date,
      };

      if (category && category === currentCategory) {
        newRow[`${prefix}_category`] = null;
        groupLists[prefix][currentCategoryStartIdx][`${prefix}_categorySpan`]++;
      } else {
        currentCategory = category;
        currentCategoryStartIdx = groupLists[prefix].length;
        newRow[`${prefix}_categorySpan`] = 1;
      }

      groupLists[prefix].push(newRow);
    }
  }

  const { search, route, map } = groupLists;
  const maxLength = Math.max(search.length, route.length, map.length);

  return Array.from({ length: maxLength }, (_, i) => {
    const row = {};
    if (search[i]) Object.assign(row, search[i]);
    if (route[i]) Object.assign(row, route[i]);
    if (map[i]) Object.assign(row, map[i]);
    return row;
  });
};

/**
 * 월 사용량과 단가, 무료 한도를 기반으로 예상 비용을 계산합니다.
 *
 * @param {number} value            - 월 사용량
 * @param {number|string} chargeValue - 건당 단가
 * @param {number} [freeValue]      - 무료 제공 한도
 * @returns {number|string}
 */
export const costCalculateValue = (value, chargeValue, freeValue) => {
  if (value === 0) return 0;
  if (isNaN(chargeValue)) return chargeValue;

  let result;
  if (freeValue === undefined || freeValue === 0) {
    result = chargeValue * value;
  } else if (value <= freeValue) {
    result = 0;
  } else {
    result = (value - freeValue) * chargeValue;
  }

  return Math.round(result);
};

/**
 * 비용 배열의 총합을 계산합니다.
 *
 * @param {Array<number|string>} cost
 * @returns {string}
 */
export const calcTotalValue = (cost) => {
  if (cost.includes('문의하기')) return '문의하기';
  if (cost.includes('개별 문의')) return '개별 문의';

  const total = cost.reduce((sum, value) => {
    const numericValue = parseFloat(
      String(value)
        .replace(/[^0-9.]/g, '')
        .replace(/,/g, '')
    );
    return sum + (isNaN(numericValue) ? 0 : numericValue);
  }, 0);

  return `${formatThreeComma(Math.round(total))}`;
};

/**
 * 계산기 테이블의 초기 비용 배열을 계산합니다.
 *
 * @returns {Array<number|string>}
 */
export const calcInitialCostValues = () => {
  const data = calcBilingPlanCalcInfo();

  const monthValues = data.flatMap((row) =>
    row
      .filter((item) => item && item.type === 'month_use')
      .map((item) => item.value)
  );
  const freeValues = data.flatMap((row) =>
    row
      .filter((item) => item && item.type === 'month_use')
      .map((item) => item.slider.exceptionLimit)
  );
  const chargeValues = data.flatMap((row) =>
    row
      .filter((item) => item && item.type === 'charge_per_call')
      .map((item) => item.value)
  );

  if (monthValues.length !== chargeValues.length) {
    throw new Error('month_use 배열과 charge_per_call 배열의 길이가 다릅니다.');
  }

  return monthValues.map((value, index) =>
    costCalculateValue(value, chargeValues[index], freeValues[index])
  );
};

/**
 * 계산기 전체의 초기 상태를 반환합니다.
 * Recoil atom default 값으로도 사용됩니다.
 *
 * @returns {{ slider: number[], cost: (number|string)[], total: string }}
 */
export const calcInitCalcInfo = () => {
  const initialCost = calcInitialCostValues();
  return {
    slider: calcBilingPlanCalcInfo().flatMap((row) =>
      row
        .filter((item) => item && item.type === 'month_use')
        .map((item) => item.value)
    ),
    cost: initialCost,
    total: calcTotalValue(initialCost),
  };
};

/**
 * 계산기 리셋 상태를 반환합니다.
 *
 * @returns {{ slider: number[], cost: (number|string)[], total: string }}
 */
export const calcResetCalcInfo = () => {
  const data = calcBilingPlanCalcInfo();

  const freeValues = data.flatMap((row) =>
    row
      .filter((item) => item && item.type === 'month_use')
      .map((item) => item.slider.exceptionLimit)
  );
  const chargeValues = data.flatMap((row) =>
    row
      .filter((item) => item && item.type === 'charge_per_call')
      .map((item) => item.value)
  );

  const slider = Array(chargeValues.length).fill(0);
  const cost = slider.map((value, index) =>
    costCalculateValue(value, chargeValues[index], freeValues[index])
  );

  return { slider, cost, total: calcTotalValue(cost) };
};

// ─── 훅 (default export) ───────────────────────────────────────────────────────

/**
 * @function useBilingCalc
 * @desc 요금 계산 데이터를 제공하는 커스텀 훅
 *
 * 정책 JSON에서 계산된 테이블 데이터를 useMemo로 캐싱해 반환합니다.
 * 컴포넌트에서 직접 calcBilingPlanCalcInfo() 등을 호출하는 대신
 * 이 훅을 사용하면 렌더링마다 재계산되지 않습니다.
 *
 * @param {'free'|'lite'|'premium'|'license'} [type] - 요금제 유형 (typeData 필요 시)
 * @returns {{
 *   tableData: Array,   - 요금 계산기 테이블 행 데이터
 *   typeData: Array,    - 요금제 유형별 테이블 행 데이터 (type 전달 시)
 * }}
 */
const useBilingCalc = (type) => {
  /** 요금 계산기 테이블용 행 데이터 (deps 없음 = 정적 JSON 기반) */
  const tableData = useMemo(() => calcBilingPlanCalcInfo(), []);

  /** 요금제 유형별 테이블 행 데이터 (type 변경 시에만 재계산) */
  const typeData = useMemo(
    () => (type ? calcBillingPlanTypeInfo(type) : []),
    [type]
  );

  return { tableData, typeData };
};

export default useBilingCalc;
